import { useState } from 'react';
import { Delete, Lock, UserRound } from 'lucide-react';
import { checkPin } from '@/data/people';
import { ROLE_LABEL } from '@/domain/permissions';
import type { Person } from '@/domain/types';
import { Badge, Button, cn } from '@/ui/core';
import { Dialog } from '@/ui/overlay';
import { useSession, useTheme } from './providers';

/** "Who is using the tablet?" — tap your name (managers with a PIN type it). */
export function WhoDialog({ open, onClose, onSwitched }: { open: boolean; onClose: () => void; onSwitched?: (p: Person) => void }) {
  const { mode, people, actor, switchUser } = useSession();
  const { pref, setPref } = useTheme();
  const [asking, setAsking] = useState<Person | null>(null);
  const choose = (p: Person) => {
    if (p.pinHash && p.id !== actor.userId) {
      setAsking(p);
      return;
    }
    switchUser(p.id);
    onSwitched?.(p);
    onClose();
  };
  const close = () => {
    setAsking(null);
    onClose();
  };
  return (
    <Dialog open={open} onClose={close} title={asking ? `PIN for ${asking.name}` : 'Who is using the tablet?'}>
      {asking ? (
        <PinPad
          person={asking}
          onCancel={() => setAsking(null)}
          onOk={() => {
            switchUser(asking.id);
            onSwitched?.(asking);
            setAsking(null);
            onClose();
          }}
        />
      ) : (
        <>
          <p className="mb-3 text-base text-muted">Tap your name.</p>
          <ul className="grid gap-2 sm:grid-cols-2">
            {people
              .filter((u) => u.active)
              .sort((a, b) => Number(a.role !== 'staff') - Number(b.role !== 'staff') || a.name.localeCompare(b.name))
              .map((u) => (
                <li key={u.id}>
                  <button type="button" onClick={() => choose(u)} className={cn('flex min-h-16 w-full items-center gap-3 rounded-2xl border-2 px-4 text-left', u.id === actor.userId ? 'border-accent bg-accent-soft' : 'border-border hover:bg-surface-2')}>
                    <UserRound className="size-6 shrink-0 text-muted" aria-hidden />
                    <span className="flex-1 text-lg font-semibold">{u.name}</span>
                    {u.pinHash && <Lock className="size-4 text-muted" aria-label="Has a PIN" />}
                    {u.role !== 'staff' && <Badge tone="accent">{ROLE_LABEL[u.role]}</Badge>}
                  </button>
                </li>
              ))}
          </ul>
          {mode === 'practice' && (
            <p className="mt-4 rounded-xl bg-surface-2 p-3 text-sm text-muted">
              <strong className="text-text">Practice mode.</strong> These are made-up people. Pick anyone.
            </p>
          )}
          <div className="mt-5 border-t border-border pt-4">
            <p className="mb-2 text-sm font-semibold">Screen colours</p>
            <div className="flex flex-wrap gap-2">
              {(['system', 'light', 'dark'] as const).map((t) => (
                <Button key={t} size="sm" variant={pref === t ? 'primary' : 'secondary'} onClick={() => setPref(t)}>
                  {t === 'system' ? 'Match the tablet' : t === 'light' ? 'Light' : 'Dark'}
                </Button>
              ))}
            </div>
          </div>
        </>
      )}
    </Dialog>
  );
}

/** Big number buttons for a 4-digit PIN. */
export function PinPad({ person, onOk, onCancel }: { person: Person; onOk: () => void; onCancel: () => void }) {
  const [pin, setPin] = useState('');
  const [wrong, setWrong] = useState(false);
  const [busy, setBusy] = useState(false);
  const press = async (d: string) => {
    if (busy) return;
    const next = (pin + d).slice(0, 4);
    setPin(next);
    setWrong(false);
    if (next.length === 4) {
      setBusy(true);
      const ok = await checkPin(person, next);
      setBusy(false);
      if (ok) onOk();
      else {
        setWrong(true);
        setPin('');
      }
    }
  };
  return (
    <div className="flex flex-col items-center gap-4">
      <div className="flex gap-3" aria-live="polite" aria-label={`${pin.length} of 4 digits typed`}>
        {[0, 1, 2, 3].map((i) => (
          <span key={i} className={cn('size-5 rounded-full border-2', i < pin.length ? 'border-accent-fill bg-accent-fill' : 'border-border')} />
        ))}
      </div>
      {wrong && (
        <p role="alert" className="font-semibold text-danger">
          That’s not the PIN. Try again.
        </p>
      )}
      <div className="grid grid-cols-3 gap-2">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
          <button key={d} type="button" onClick={() => void press(d)} className="size-18 rounded-2xl border-2 border-border bg-surface text-3xl font-bold hover:bg-surface-2 active:scale-95">
            {d}
          </button>
        ))}
        <button type="button" onClick={onCancel} className="size-18 rounded-2xl text-base font-semibold text-muted hover:bg-surface-2">
          Cancel
        </button>
        <button type="button" onClick={() => void press('0')} className="size-18 rounded-2xl border-2 border-border bg-surface text-3xl font-bold hover:bg-surface-2 active:scale-95">
          0
        </button>
        <button type="button" onClick={() => setPin((p) => p.slice(0, -1))} aria-label="Delete the last digit" className="flex size-18 items-center justify-center rounded-2xl text-muted hover:bg-surface-2">
          <Delete className="size-7" aria-hidden />
        </button>
      </div>
    </div>
  );
}
